import React, { useEffect, useMemo } from "react";
import LayoutHOC from "../LayoutHOC";
import { ReactComponent as GmailIcon } from "../../assets/images/icons/GmailIcon.svg";
import { ReactComponent as Location } from "../../assets/images/icons/Location.svg";
import { ReactComponent as EmptyNotes } from "../../assets/images/icons/EmptyNotes.svg";
import { ReactComponent as EmptyAttachments } from "../../assets/images/icons/EmptyAttachments.svg";
import { ReactComponent as EditPencil } from "../../assets/images/disableImg.svg";
import BackArrowCompoentIcon from "../../assets/images/icons/BackArrowCompoentIcon";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  useGetAddressQuery,
  useGetContactQuery,
  useGetFinancialDetailsQuery,
  useGetVendorByIdQuery,
} from "../../utils/customer/customerTaskTable";
import { getUserToken } from "../../utils/utilityFunctions/localServices";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../utils/loaderSlice";
import { pushAlert } from "../../utils/alertSlice";

const VendorProfile = ({
  editData,
  GeneralInfoData,
  addressData: propAddressData,
  contactData: propContactData,
  financialDetailsData: propFinancialDetailsData,
  setEditSection,
  setActiveTab,
  setIsEditVendor,
}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const userToken = getUserToken();
  const dispatch = useDispatch();

  // Fetch vendor details using id from useParams
  const {
    data: vendorDetailsResponse,
    isLoading: isVendorLoading,
    isSuccess: isVendorSuccess,
    isError: isVendorError,
    error: vendorError,
    refetch: refetchVendorDetails,
  } = useGetVendorByIdQuery({ UUID: id, token: userToken }, { skip: !id });

  // Fetch address data using id from useParams
  const {
    data: fetchedAddressData,
    isLoading: isAddressLoading,
    isError: isAddressError,
    error: addressError,
    refetch: refetchAddress,
  } = useGetAddressQuery({ UUID: id, token: userToken }, { skip: !id });

  // Fetch contact data using id from useParams
  const {
    data: fetchedContactData,
    isLoading: isContactLoading,
    isError: isContactError,
    error: contactError,
    refetch: refetchContact,
  } = useGetContactQuery({ UUID: id, token: userToken }, { skip: !id });

  // Fetch financial details using id from useParams
  const {
    data: fetchedFinancialDetailsData,
    isLoading: isFinancialLoading,
    isError: isFinancialError,
    error: financialError,
    refetch: refetchFinancial,
  } = useGetFinancialDetailsQuery({ UUID: id, token: userToken }, { skip: !id });

  // Prioritize vendorDetailsResponse?.data over location.state or props
  const vendorData = useMemo(
    () => vendorDetailsResponse?.data || location.state?.vendorData || editData || GeneralInfoData?.data || {},
    [vendorDetailsResponse, location.state?.vendorData, editData, GeneralInfoData]
  );

  // Use props or fetched data, with fallback to location.state
  const addressData = useMemo(
    () => propAddressData || fetchedAddressData || location.state?.addressData || { data: [] },
    [propAddressData, fetchedAddressData, location.state?.addressData]
  );
  const contactData = useMemo(
    () => propContactData || fetchedContactData || location.state?.contactData || { data: [] },
    [propContactData, fetchedContactData, location.state?.contactData]
  );
  const financialDetailsData = useMemo(
    () =>
      propFinancialDetailsData || fetchedFinancialDetailsData || location.state?.financialDetailsData || { data: [] },
    [propFinancialDetailsData, fetchedFinancialDetailsData, location.state?.financialDetailsData]
  );

  // Always refetch data on mount or id change
  useEffect(() => {
    if (id) {
      refetchVendorDetails();
      refetchAddress();
      refetchContact();
      refetchFinancial();
    }
  }, [id, refetchVendorDetails, refetchAddress, refetchContact, refetchFinancial]);

  // Handle loading and error states
  useEffect(() => {
    const isLoading = isVendorLoading || isAddressLoading || isContactLoading || isFinancialLoading;
    if (isLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }

    if (isVendorError) {
      dispatch(
        pushAlert({
          message: vendorError?.data?.message || "Failed to fetch vendor details",
          type: "danger",
          visible: true,
        })
      );
    }
    if (isAddressError) {
      dispatch(
        pushAlert({
          message: addressError?.data?.message || "Failed to fetch address data",
          type: "danger",
          visible: true,
        })
      );
    }
    if (isContactError) {
      dispatch(
        pushAlert({
          message: contactError?.data?.message || "Failed to fetch contact data",
          type: "danger",
          visible: true,
        })
      );
    }
    if (isFinancialError) {
      dispatch(
        pushAlert({
          message: financialError?.data?.message || "Failed to fetch financial details",
          type: "danger",
          visible: true,
        })
      );
    }
  }, [
    isVendorLoading,
    isAddressLoading,
    isContactLoading,
    isFinancialLoading,
    isVendorError,
    isAddressError,
    isContactError,
    isFinancialError,
    vendorError,
    addressError,
    contactError,
    financialError,
    dispatch,
  ]);

  // Format address data
  const formattedAddress = useMemo(() => {
    if (!addressData?.data || !Array.isArray(addressData.data)) {
      console.log("formattedAddress: No address data or invalid format", addressData);
      return { billing: null, shipping: null };
    }

    const addresses = addressData.data;

    let billing = addresses.find(
      (a) => a.address_type === "billing" && (a.is_primary === 1 || a.is_primary === true || a.is_primary === "1")
    );
    if (!billing) {
      billing = addresses.find((a) => a.address_type === "billing");
    }

    let shipping = addresses.find(
      (a) => a.address_type === "shipping" && (a.is_primary === 1 || a.is_primary === true || a.is_primary === "1")
    );
    if (!shipping) {
      shipping = addresses.find((a) => a.address_type === "shipping");
    }

    return {
      billing: billing
        ? {
            id: billing.id || "",
            point_of_contact: billing.point_of_contact || "--",
            address_line1: billing.address_line1 || "--",
            address_line2: billing.address_line2 || "--",
            city: billing.city || "--",
            state: billing.state || "--",
            country: billing.country || "--",
            phone_number: billing.phone_number || "--",
            is_primary: billing.is_primary || 0,
            address_group_id: billing.address_group_id || "",
          }
        : null,
      shipping: shipping
        ? {
            id: shipping.id || "",
            point_of_contact: shipping.point_of_contact || "--",
            address_line1: shipping.address_line1 || "--",
            address_line2: shipping.address_line2 || "--",
            city: shipping.city || "--",
            state: shipping.state || "--",
            country: shipping.country || "--",
            phone_number: shipping.phone_number || "--",
            is_primary: shipping.is_primary || 0,
            address_group_id: shipping.address_group_id || "",
          }
        : null,
    };
  }, [addressData]);

  // Format contact data
  const formattedContact = useMemo(() => {
    if (!contactData?.data || !Array.isArray(contactData.data) || contactData.data.length === 0) {
      console.log("formattedContact: No contact data or empty array", contactData);
      return null;
    }

    const contacts = contactData.data;

    let contact = contacts.find((c) => c.is_primary === 1 || c.is_primary === true || c.is_primary === "1");
    if (!contact) {
      contact = contacts[0];
    }

    return {
      id: contact?.id || "",
      name: contact?.name || "--",
      email: contact?.email || "--",
      phone: contact?.phone || "--",
      is_primary: contact?.is_primary || 0,
    };
  }, [contactData]);

  // Format financial data
  const formattedFinancial = useMemo(() => {
    if (
      !financialDetailsData?.data ||
      !Array.isArray(financialDetailsData.data) ||
      financialDetailsData.data.length === 0
    ) {
      console.log("formattedFinancial: No financial data or empty array", financialDetailsData);
      return null;
    }

    const financials = financialDetailsData.data;

    let financial = financials.find((f) => f.is_primary === 1 || f.is_primary === true || f.is_primary === "1");
    if (!financial) {
      financial = financials[0];
    }

    return {
      id: financial?.id || "",
      bank_account_name: financial?.bank_account_name || "--",
      bank_account_number: financial?.bank_account_number || "--",
      routing_number: financial?.routing_number || "--",
      payment_terms: financial?.payment_terms || "--",
      opening_balance: financial?.opening_balance ? parseFloat(financial.opening_balance).toFixed(2) : "--",
      is_primary: financial?.is_primary || 0,
    };
  }, [financialDetailsData]);

  const handleEditGeneralInfo = () => {
    if (typeof setEditSection === "function") setEditSection(true);
    if (typeof setActiveTab === "function") setActiveTab("general");
    if (typeof setIsEditVendor === "function") setIsEditVendor(true);
    navigate("/edit-vendor", {
      state: {
        vendorData: {
          ...vendorData,
          ...vendorDetailsResponse?.data,
          row_id: vendorData?.row_id || vendorDetailsResponse?.data?.row_id || id,
        },
        addressData: formattedAddress,
        contactData: formattedContact,
        financialData: formattedFinancial,
        isViewMode: false,
        activeTab: "general",
        isShowMode: true,
        isEditMode: true,
      },
    });
  };

  const handleEditAddress = () => {
    navigate("/edit-vendor", {
      state: {
        vendorData: {
          ...vendorData,
          ...vendorDetailsResponse?.data,
          row_id: vendorData?.row_id || vendorDetailsResponse?.data?.row_id || id,
        },
        addressData: formattedAddress,
        contactData: formattedContact,
        financialData: formattedFinancial,
        isViewMode: false,
        activeTab: "address",
        isShowMode: true,
        isEditMode: true,
      },
    });
  };

  const handleEditContact = () => {
    navigate("/edit-vendor", {
      state: {
        vendorData: {
          ...vendorData,
          ...vendorDetailsResponse?.data,
          row_id: vendorData?.row_id || vendorDetailsResponse?.data?.row_id || id,
        },
        addressData: formattedAddress,
        contactData: formattedContact,
        financialData: formattedFinancial,
        isViewMode: false,
        activeTab: "contact",
        isShowMode: true,
        isEditMode: true,
      },
    });
  };

  const handleEditFinancial = () => {
    navigate("/edit-vendor", {
      state: {
        vendorData: {
          ...vendorData,
          ...vendorDetailsResponse?.data,
          row_id: vendorData?.row_id || vendorDetailsResponse?.data?.row_id || id,
        },
        addressData: formattedAddress,
        contactData: formattedContact,
        financialData: formattedFinancial,
        isViewMode: false,
        activeTab: "financial",
        isShowMode: true,
        isEditMode: true,
      },
    });
  };

  const handleEditNotes = () => {
    navigate("/edit-vendor", {
      state: {
        vendorData: {
          ...vendorData,
          ...vendorDetailsResponse?.data,
          row_id: vendorData?.row_id || vendorDetailsResponse?.data?.row_id || id,
        },
        addressData: formattedAddress,
        contactData: formattedContact,
        financialData: formattedFinancial,
        isViewMode: false,
        activeTab: "notes",
        isShowMode: true,
        isEditMode: true,
      },
    });
  };

  const handleEditAttachments = () => {
    navigate("/edit-vendor", {
      state: {
        vendorData: {
          ...vendorData,
          ...vendorDetailsResponse?.data,
          row_id: vendorData?.row_id || vendorDetailsResponse?.data?.row_id || id,
        },
        addressData: formattedAddress,
        contactData: formattedContact,
        financialData: formattedFinancial,
        isViewMode: false,
        activeTab: "attachments",
        isShowMode: true,
        isEditMode: true,
      },
    });
  };

  console.log(vendorDetailsResponse, "vendorDetailsResponse4535");

  return (
    <div className="h-full w-full flex flex-col bg-white">
      <>
        <div className="w-full p-3 !pb-0 text-[#313131] text-16 font-500 h-fit text-wrap flex items-center gap-3">
          <Link to="/vendors">
            <BackArrowCompoentIcon />
          </Link>
          <span className="items-start">Vendor Dashboard</span>
        </div>

        <div className="flex p-3 !pt-0 w-full gap-3 min-h-0 grow">
          <div className="w-1/3 h-full flex flex-col gap-3 min-w-0">
            <div className="border rounded-md p-4 flex gap-6 bg-white h-fit sticky-top min-w-0">
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-blue-500 text-white text-xl shrink-0 overflow-hidden">
                {vendorDetailsResponse?.data?.vendor_logo_url ? (
                  <img
                    src={vendorDetailsResponse?.data?.vendor_logo_url}
                    alt="Business Logo"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="font-semibold">
                    {(vendorData?.vendor_name || vendorDetailsResponse?.data?.vendor_name || "NA")
                      .slice(0, 2)
                      .toUpperCase()}
                  </span>
                )}
              </div>

              <div className="min-w-0 flex flex-col grow">
                <h3 className="text-lg font-semibold text-[#383838] truncate mb-1">
                  {vendorData?.vendor_name || vendorDetailsResponse?.data?.vendor_name || "--"}
                </h3>
                <div className="flex gap-1 text-sm text-[#666] min-w-0">
                  <span className="flex gap-1 items-center text-wrap break-words w-1/2 min-w-0">
                    <GmailIcon className="shrink-0" />
                    <span className="truncate text-[#007AFF] text-14 font-400 items-center min-w-0 border-b border-[#007AFF]">
                      {formattedContact?.email || "--"}
                    </span>
                  </span>
                  <span className="flex gap-1 items-center text-wrap break-words w-1/2 min-w-0">
                    <Location className="shrink-0" />
                    <span className="truncate text-14 font-400 items-center min-w-0 text-[#131313cc]">
                      {vendorDetailsResponse?.data?.country || "--"}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            <div className="flex h-full w-full min-w-0">
              {/* Content area */}
              <div className="flex-1 flex flex-col gap-1 min-w-0">
                {/* Scrollable sections */}
                <div className="flex flex-col h-full min-w-0 hover:overflow-y-auto overflow-y-hidden thinscrollbar">
                  {/* General Info */}
                  <div className="border-dashed border-b p-4 bg-white h-full min-w-0 group hover:bg-[#759fff0f] transition-colors">
                    <div className="flex justify-between items-center mb-3 h-fit">
                      <h3 className="font-semibold text-[#1D4469] text-16">General Info</h3>
                      <button
                        onClick={handleEditGeneralInfo}
                        className="text-[#13131399] text-14 font-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <EditPencil />
                      </button>
                    </div>
                    <div className="flex gap-6 min-w-0 min-h-0 grow">
                      {/* Left column */}
                      <div className="w-1/2 text-sm text-[#666] space-y-6 min-w-0 h-full">
                        <div>
                          <label className="text-[#13131399] text-14 font-400">Name</label>
                          <div className="text-[#131313cc] text-14 font-500 truncate w-full overflow-hidden whitespace-nowrap">
                            {vendorData?.vendor_name || vendorDetailsResponse?.data?.vendor_name || "--"}
                          </div>
                        </div>
                        <div>
                          <label className="text-[#13131399] text-14 font-400">Website</label>
                          <div className="text-[#131313cc] text-14 font-500 truncate w-full overflow-hidden whitespace-nowrap">
                            {vendorData?.website || vendorDetailsResponse?.data?.website || "--"}
                          </div>
                        </div>
                        <div>
                          <label className="text-[#13131399] text-14 font-400">Country</label>
                          <div className="text-[#131313cc] text-14 font-500 truncate w-full overflow-hidden whitespace-nowrap">
                            {vendorData?.country || vendorDetailsResponse?.data?.country || "--"}
                          </div>
                        </div>
                        <div>
                          <label className="text-[#13131399] text-14 font-400">Currency</label>
                          <div className="text-[#131313cc] text-14 font-500 truncate w-full overflow-hidden whitespace-nowrap">
                            {vendorDetailsResponse?.data?.currency || "--"}
                          </div>
                        </div>
                      </div>
                      {/* Right column */}
                      <div className="w-1/2 text-sm text-[#666] space-y-6 min-w-0 h-full">
                        <div>
                          <label className="text-[#13131399] text-14 font-400">Display Name</label>
                          <div className="text-[#131313cc] text-14 font-500 truncate w-full overflow-hidden whitespace-nowrap">
                            {vendorDetailsResponse?.data?.vendor_display_name || "--"}
                          </div>
                        </div>
                        <div>
                          <label className="text-[#13131399] text-14 font-400">Business Category</label>
                          <div className="text-[#131313cc] text-14 font-500 truncate w-full overflow-hidden whitespace-nowrap">
                            {vendorData?.business_category || vendorDetailsResponse?.data?.business_category || "--"}
                          </div>
                        </div>
                        <div>
                          <label className="text-[#13131399] text-14 font-400">Social Security Number</label>
                          <div className="text-[#131313cc] text-14 font-500 truncate w-full overflow-hidden whitespace-nowrap">
                            {vendorDetailsResponse?.data?.business_id || "--"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Address Info */}
                  <div className="border-dashed border-b p-4 bg-white h-full min-w-0 group hover:bg-[#759fff0f] transition-colors">
                    <div className="flex justify-between items-center">
                      <h3 className="font-semibold text-[#1D4469] text-16">Address Info</h3>
                      <button
                        onClick={handleEditAddress}
                        className="text-[#2999BC] text-sm flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <EditPencil />
                      </button>
                    </div>
                    {formattedAddress?.billing || formattedAddress?.shipping ? (
                      <div className="flex justify-between gap-10 w-full h-full min-w-0">
                        <div className="text-sm text-[#666] mt-2 w-1/2 h-full break-words">
                          <h4 className="font-12 font-500 text-[#13131399] mb-2">Billing Address</h4>
                          {formattedAddress?.billing ? (
                            <>
                              <p className="text-[#007AFF] text-14 font-500 mb-1">
                                {formattedAddress.billing.point_of_contact}
                              </p>
                              <p className="text-[#131313cc] text-14 font-500 whitespace-normal break-all mb-1">
                                {formattedAddress.billing.address_line1} <br />
                                {formattedAddress.billing.address_line2 || ""}
                              </p>
                              <p className="text-[#131313cc] text-14 font-500 whitespace-normal break-all mb-1">
                                {formattedAddress.billing.city} {formattedAddress.billing.state}{" "}
                                {formattedAddress.billing.country}
                              </p>
                              <p className="text-[#131313cc] text-14 font-500 whitespace-normal break-all mb-1">
                                {formattedAddress.billing.phone_number}
                              </p>
                            </>
                          ) : (
                            <p className="text-[#999]">--</p>
                          )}
                        </div>
                        <div className="text-sm text-[#666] mt-2 w-1/2 h-full break-words">
                          <h4 className="font-12 font-500 text-[#13131399] mb-2">Shipping Address</h4>
                          {formattedAddress?.shipping ? (
                            <>
                              <p className="text-[#007AFF] text-14 font-500 mb-1">
                                {formattedAddress.shipping.point_of_contact}
                              </p>
                              <p className="text-[#131313cc] text-14 font-500 whitespace-normal break-all">
                                {formattedAddress.shipping.address_line1} <br />
                                {formattedAddress.shipping.address_line2 || ""}
                              </p>
                              <p className="text-[#131313cc] text-14 font-500 whitespace-normal break-all">
                                {formattedAddress.shipping.city} {formattedAddress.shipping.state}{" "}
                                {formattedAddress.shipping.country}
                              </p>
                              <p className="text-[#131313cc] text-14 font-500 whitespace-normal break-all">
                                {formattedAddress.shipping.phone_number}
                              </p>
                            </>
                          ) : (
                            <p className="text-[#999]">--</p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="text-[#999] mt-2">No data available!</p>
                    )}
                  </div>

                  {/* Contact Info */}
                  <div className="border-dashed border-b p-4 bg-white h-full min-w-0 group hover:bg-[#759fff0f] transition-colors">
                    <div className="flex justify-between items-center">
                      <h3 className="font-semibold text-[#1D4469] text-16">Contact Info</h3>
                      <button
                        onClick={handleEditContact}
                        className="text-[#2999BC] text-sm flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <EditPencil />
                      </button>
                    </div>
                    {formattedContact ? (
                      <div className="flex gap-6 min-w-0 mt-2">
                        {/* Left column */}
                        <div className="w-1/2 text-sm text-[#666] space-y-6 min-w-0 h-full">
                          <div>
                            <label className="text-[#13131399] text-14 font-400">Name</label>
                            <div className="text-[#131313cc] text-14 font-500 truncate w-full overflow-hidden whitespace-nowrap">
                              {formattedContact?.name || "--"}
                            </div>
                          </div>
                          <div>
                            <label className="text-[#13131399] text-14 font-400">Phone</label>
                            <div className="text-[#131313cc] text-14 font-500 truncate w-full overflow-hidden whitespace-nowrap">
                              {formattedContact?.phone || "--"}
                            </div>
                          </div>
                        </div>
                        {/* Right column */}
                        <div className="w-1/2 text-sm text-[#666] space-y-6 min-w-0 h-full">
                          <div>
                            <label className="text-[#13131399] text-14 font-400">Email</label>
                            <div className="text-[#131313cc] text-14 font-500 truncate w-full overflow-hidden whitespace-nowrap">
                              {formattedContact?.email || "--"}
                            </div>
                          </div>
                          {formattedContact?.is_primary === 1 && (
                            <div className="text-[#2999BC] text-14 font-400">Primary Contact</div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-[#999] mt-2">No contact available!</div>
                    )}
                  </div>

                  {/* Financial Detail */}
                  <div className="border-dashed border-b p-4 bg-white h-full group hover:bg-[#759fff0f] transition-colors">
                    <div className="flex justify-between items-center">
                      <h3 className="font-semibold text-[#1D4469] text-16">Financial Detail</h3>
                      <button
                        onClick={handleEditFinancial}
                        className="text-[#2999BC] text-sm flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <EditPencil />
                      </button>
                    </div>
                    {formattedFinancial ? (
                      <div className="grid grid-cols-2 gap-6 text-sm text-[#666] mt-2 min-w-0">
                        <div>
                          <label className="text-[#13131399] text-14 font-400">Bank Account Name</label>
                          <div className="text-[#131313cc] text-14 font-500 truncate w-full overflow-hidden whitespace-nowrap">
                            {formattedFinancial?.bank_account_name || "--"}
                          </div>
                        </div>
                        <div>
                          <label className="text-[#13131399] text-14 font-400">Bank Account Number</label>
                          <div className="text-[#131313cc] text-14 font-500 truncate w-full overflow-hidden whitespace-nowrap">
                            {formattedFinancial?.bank_account_number || "--"}
                          </div>
                        </div>
                        <div>
                          <label className="text-[#13131399] text-14 font-400">Routing Number</label>
                          <div className="text-[#131313cc] text-14 font-500 truncate w-full overflow-hidden whitespace-nowrap">
                            {formattedFinancial?.routing_number || "--"}
                          </div>
                        </div>
                        <div>
                          <label className="text-[#13131399] text-14 font-400">Payment Terms</label>
                          <div className="text-[#131313cc] text-14 font-500 truncate w-full overflow-hidden whitespace-nowrap">
                            {formattedFinancial?.payment_terms || "--"}
                          </div>
                        </div>
                        <div>
                          <label className="text-[#13131399] text-14 font-400">Opening Balance</label>
                          <div className="text-[#131313cc] text-14 font-500 truncate w-full overflow-hidden whitespace-nowrap">
                            {formattedFinancial?.opening_balance || "--"}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-[#999] mt-2">No financial details available!</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Scrollbar area (fixed width) */}
              {/* <div className="w-1 overflow-y-auto bg-transparent hover:bg-gray-100 rounded">
    
  </div> */}
            </div>
          </div>

          <div className="w-1/3 border rounded-md p-3 bg-white flex flex-col min-w-0 h-full">
            <div className="border-dashed border-b bg-white flex flex-col grow min-w-0 h-3/6">
              <div className="flex justify-between items-center mb-2">
                <div className="flex gap-3 items-center">
                  <h3 className="font-semibold text-[#313131]">Notes</h3>
                  <button onClick={handleEditNotes} className="text-[#2999BC] text-sm flex items-center gap-1">
                    <EditPencil /> Edit
                    {/* Add */}
                  </button>
                </div>
                <div>
                  <button className="text-[#666666] text-12 font-400">Show All</button>
                </div>
              </div>
              <div className="flex-grow flex items-center justify-center text-[#999] text-wrap break-words">
                <div className="flex-grow flex flex-col items-center justify-center text-[#999] text-center">
                  <EmptyNotes />
                  <h4 className="text-[#666] text-12 font-400 mt-2">Currently, there are no notes available</h4>
                  <button className="mt-2 border border-solid border-[#D2DAE1] hover:border-[#3366d999] p-2 text-[#666] hover:text-[#3366D9] rounded">
                    <i className="bi bi-plus-lg"></i> Add Notes
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white flex flex-col grow pt-3 min-w-0">
              <div className="flex justify-between items-center mb-2">
                <div className="flex gap-3 items-center">
                  <h3 className="font-semibold text-[#1D4469] text-16">Attachments</h3>
                  {/* <button onClick={handleEditAttachments} className="text-[#2999BC] text-sm flex items-center gap-1">
                    <EditPencil />
                  </button> */}
                </div>
                <div>
                  <button className="text-[#666666] text-12 font-400">Show All</button>
                </div>
              </div>
              <div className="flex-grow flex items-center justify-center text-[#999] text-wrap break-words">
                <div className="flex-grow flex flex-col items-center justify-center text-[#999] text-center">
                  <EmptyAttachments />
                  <h4 className="text-[#666] text-12 font-400 mt-2">Currently, there are no attachments available</h4>
                  <button className="mt-2 border border-solid border-[#D2DAE1] hover:border-[#3366d999] p-2 text-[#666] hover:text-[#3366D9] rounded">
                    <i className="bi bi-plus-lg"></i> Add Attachments
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="w-1/3 border rounded-md bg-white flex flex-col min-w-0">
            <h3 className="font-semibold border-b p-3">Balance Summary</h3>
            <div className="text-2xl font-bold text-green-600 p-3 text-wrap">
              4.500 USD <span className="text-black text-sm">Total</span>
            </div>
            <div className="flex gap-2 mt-1 px-3">
              <div className="flex-1 bg-green-100 text-green-800 p-2 rounded-md text-center break-words">
                <div className="text-xs">Opening Balance</div>
                <div className="font-semibold">0.00</div>
              </div>
              <div className="flex-1 bg-red-100 text-red-800 p-2 rounded-md text-center break-words">
                <div className="text-xs">Overdue Balance</div>
                <div className="font-semibold">0.00</div>
              </div>
            </div>

            <h4 className="mt-3 mb-2 font-semibold px-3">Recent Bills</h4>
            <div className="flex flex-col gap-2 text-sm px-3 min-w-0">
              {Array(6)
                .fill(0)
                .map((_, i) => (
                  <div
                    key={i}
                    className="flex justify-between items-center border-dashed border-b pb-1 text-wrap break-words"
                  >
                    <div className="min-w-0">
                      <div className="text-14 font-400 text-[#666] truncate"></div>
                      <div className="text-12 text-[#666666cc] font-400 truncate">
                        Due: NO. INVA 4257-01Sept 1, 2025
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[#219610] text-600 text-14 font-600 truncate">4.500 USD</div>
                      <div className="text-12 font-400 text-[#219610] bg-[#D3F6CE] border rounded-md px-2 py-0.5 flex justify-center">
                        Paid
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </>
    </div>
  );
};

export default LayoutHOC(VendorProfile);
